import React, { useState, useEffect } from "react";
import "../../css/CRUDModal.css";
import { axiosPostApi } from "../../helpers/interceptorServices";
import { apiList } from "../../helpers/apiList";
import CustomAlert from "../../helpers/CustomAlert";
import confetti from "canvas-confetti";
import { useDispatch } from "react-redux";
import { setBalance } from "../store/balance/balanceSlice";
import { formatDateToYYYYMMDD } from "../../helpers/DateHelpers";

const TopUpRCSModal = ({ show, handleClose, customerData, fetchData }) => {
  const loggedInUserName = sessionStorage.getItem("username");
  const [formData, setFormData] = useState({
    topupAmount: "",
    totalSmsCount: "",
    gst: "",
    totalAmount: "",
  });
  const [errors, setErrors] = useState({
    topupAmount: "",
  });
  const dispatch = useDispatch();

  const modalTitle = "Top-up RCS Request";

  useEffect(() => {
    // Reset form when modal is opened or closed
    setFormData({
      topupAmount: "",
      totalSmsCount: "",
    });
    setErrors({ topupAmount: "" });
  }, [show]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;

    if (id === "topupAmount") {
      const updatedValue = value.replace(/[^0-9.]/g, ""); // Only allow numbers and decimals
      const amount = parseFloat(updatedValue) || 0; // Convert to number for calculations

      // Calculate Total SMS Count based on the rate
      const totalSmsCount = (amount * customerData?.rcsRate) / 100; // Integer value for SMS count

      // Calculate GST at 18% of the amount
      const gst = parseFloat((totalSmsCount * 0.18).toFixed(2)); // Save GST as float with 2

      // Calculate Total Amount = Amount + GST
      const totalAmount = parseFloat((totalSmsCount + gst).toFixed(2)); // Save totalAmount as float

      setFormData({
        ...formData,
        [id]: updatedValue,
        totalSmsCount: parseFloat(totalSmsCount.toFixed(2)),
        gst,
        totalAmount,
      });
    }
  };

  const validate = () => {
    const newErrors = {};

    // Top-up Amount Validation
    if (!formData.topupAmount) {
      newErrors.topupAmount = "Top-up Amount is required";
    } else if (!/^\d{1,7}(\.\d{1,2})?$/.test(formData.topupAmount)) {
      newErrors.topupAmount =
        "Amount must be a number with a maximum of 7 digits and up to 2 decimals";
    }

    return newErrors;
  };

  const fetchDashboardData = () => {
    const endDate = formatDateToYYYYMMDD(new Date());
    const startDate1 = new Date(new Date().setDate(new Date().getDate() - 90));
    const finalStartDate = formatDateToYYYYMMDD(startDate1);

    const requestBody = {
      startDate: finalStartDate,
      endDate: endDate,
      username: loggedInUserName,
    };

    axiosPostApi(apiList.DASHBOARD_DATA, requestBody)
      .then((response) => {
        if (response?.status === 200 && response?.data?.respCode === "00") {
          dispatch(setBalance(response?.data?.data?.balanceDetails));
          fetchData(1, customerData?.customerCode);
        } else {
          // Handle error case here if necessary
        }
      })
      .catch((error) => {
        console.error("Error fetching SMS overview data", error);
      });
  };

  const handleSubmit = () => {
    const validationErrors = validate();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      const payload = {
        username: loggedInUserName,
        customerCode: customerData?.customerCode,
        customerName: customerData?.name,
        oldRcsBalance: customerData?.rcsBalance || 0,
        rate: customerData?.rcsRate,
        topupAmt: Number(formData.totalSmsCount),
        topupRcs: Number(formData.topupAmount),
        gstAmount: formData?.gst,
        totalAmount: formData?.totalAmount,
      };

      axiosPostApi(apiList.CREATE_TOPUP_RCS, payload)
        .then((response) => {
          if (response?.status === 200 && response?.data?.status === true) {
            CustomAlert({
              title: `${modalTitle} created successfully.`,
              text: response.data.message,
              icon: "success",
              confirmButtonText: "Ok",
              showCloseButton: true,
              allowOutsideClick: false,
            }).then((result) => {
              if (result.isConfirmed) {
                fetchDashboardData();
              }
            });
            // Add confetti effect
            const canvas = document.createElement("canvas");
            canvas.style.position = "fixed";
            canvas.style.top = "0";
            canvas.style.left = "0";
            canvas.style.width = "100%";
            canvas.style.height = "100%";
            canvas.style.zIndex = "3000";
            document.body.appendChild(canvas);

            const confettiInstance = confetti.create(canvas, {
              resize: true,
              useWorker: true,
            });

            confettiInstance({
              particleCount: 250,
              spread: 100,
              origin: { y: 0.6 },
            });

            setTimeout(() => {
              document.body.removeChild(canvas);
            }, 3000);

            handleClose();
          } else if (response?.status === 200 && !response?.data?.status) {
            CustomAlert({
              title: "Failed to Request",
              text: response.data.message,
              icon: "error",
              confirmButtonText: "Retry",
            });
          }

          if (response?.data?.respCode === "02") {
            CustomAlert({
              title: "Warning",
              text:
                response?.data?.message || "Unable to proceed, Request again",
              icon: "warning",
              confirmButtonText: "Okay",
            });
          }
        })
        .catch((error) => {
          console.error("Error processing RCS request:", error);
          CustomAlert({
            title: "Error",
            text: "An error occurred while adding the RCS request. Please try again later.",
            icon: "error",
            confirmButtonText: "Retry",
          });
        });
    }
  };

  const handleBack = () => {
    handleClose();
    setErrors({});
    setFormData({
      topupAmount: "",
      totalSmsCount: "",
      gst: "",
      totalAmount: "",
    });
  };

  return (
    <>
      <div
        className={`modal-lg modal fade ${show ? "show" : ""}`}
        tabIndex="-1"
        role="dialog"
        style={{ display: show ? "block" : "none" }}
      >
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title main-title">{modalTitle}</h5>
            </div>
            <form>
              <div className="scoped-modal modal-body">
                <div className="row mb-3">
                  {/* <div className="col-md-4">
                    <div className="form-group">
                      <label htmlFor="mainWalletBalance">
                        RCS Wallet Balance
                        <span style={{ color: "red" }}>*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="mainWalletBalance"
                        placeholder="0"
                        value={customerData?.rcsBalance || "0"}
                        style={{ textAlign: "right", cursor: "not-allowed" }}
                        readOnly={true}
                      />
                    </div>
                  </div> */}
                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="oldSmsCount">Old RCS Count</label>
                      <input
                        type="text"
                        className="form-control"
                        id="oldSmsCount"
                        placeholder="0"
                        value={customerData?.rcsBalance || "0"}
                        style={{ textAlign: "right", cursor: "not-allowed" }}
                        readOnly={true}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="rate">Rate <span style={{color:"black"}}>(in paisa)</span></label>
                      <input
                        type="text"
                        className="form-control"
                        id="rate"
                        placeholder="0.00"
                        value={Number(customerData?.rcsRate).toFixed(2)}
                        style={{ textAlign: "right", cursor: "not-allowed" }}
                        readOnly={true}
                      />
                    </div>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label htmlFor="topupAmount">
                      Top-up RCS Quantity<span style={{ color: "red" }}>*</span>
                    </label>
                    <div className="form-group">
                      <input
                        type="text"
                        className="form-control"
                        id="topupAmount"
                        placeholder="0"
                        value={formData.topupAmount}
                        style={{ textAlign: "right" }}
                        onChange={handleInputChange}
                      />
                      {errors.topupAmount ? (
                        <span className="error">{errors.topupAmount}</span>
                      ) : null}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="totalSmsCount">Top-up Amount</label>
                      <input
                        type="text"
                        className="form-control"
                        id="totalSmsCount"
                        placeholder="0.00"
                        value={formData.totalSmsCount}
                        style={{ textAlign: "right", cursor: "not-allowed" }}
                        readOnly={true}
                      />
                    </div>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="gst">GST Amount (18%)</label>
                      <input
                        type="text"
                        className="form-control"
                        id="gst"
                        placeholder="0.00"
                        value={formData?.gst || ""}
                        // disabled={isViewMode || isDeleteMode}
                        style={{
                          textAlign: "right",
                          cursor: "not-allowed",
                        }}
                        onChange={handleInputChange}
                        readOnly
                      />
                      {errors.gst ? (
                        <span className="error">{errors.gst}</span>
                      ) : null}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="totalAmount">Total Amount</label>
                      <input
                        type="text"
                        className="form-control"
                        id="totalAmount"
                        placeholder="0.00"
                        value={formData.totalAmount}
                        style={{ textAlign: "right", cursor: "not-allowed" }}
                        readOnly={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="scoped-modal modal-footer">
                <button
                  type="button"
                  className="btn btn-back"
                  onClick={handleBack}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="btn btn-submit"
                  onClick={handleSubmit}
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {show && <div className="modal-backdrop fade show"></div>}
    </>
  );
};

export default TopUpRCSModal;
